import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import { getCdnImage } from '../lib/imageUtils';

export default function About() {
  return (
    <div className="bg-slate-50 min-h-screen">
      <SeoMeta
        title="Om os - ENTREPRENØR LANG ApS"
        description="Entreprenør Lang blev oprettet i marts 2005 af Martin Hovmand Lang som et enmandsfirma beliggende i Øster Ulslev."
        keywords="om os, entreprenør lang, martin hovmand lang, karlebyvej 32, øster ulslev, kloakmester"
      />

      {/* Hero */}
      <section className="bg-slate-900 text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/firma2.jpg', {
              width: 1920,
              quality: 80,
            })}
            alt="ENTREPRENØR LANG ApS Karlebyvej 32"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-3 border border-emerald-500/30">
              <i className="fa-solid fa-hard-hat" aria-hidden="true" />
              Siden marts 2005
            </span>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
              Om ENTREPRENØR LANG
            </h1>
            <p className="text-slate-300 text-base sm:text-lg mt-3 leading-relaxed">
              Grundlagt med passion for godt håndværk, pålidelighed og personlig kontakt til hver eneste kunde.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Scrape prose text (verbatim) */}
          <div className="lg:col-span-7 space-y-6">
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-5">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Vores historie og profil
              </h2>

              <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4 text-base">
                <p>
                  Entreprenør Lang blev oprettet i marts 2005 af Martin Hovmand Lang, som valgte at starte firmaet helt fra bunden.
                </p>
                <p>
                  Firmaet blev startet som et enmandsfirma, hvilket det fortsat er.
                </p>
                <p>
                  Firmaet drives fra adressen Karlebyvej 32, 4894 Øster Ulslev, som også er hjem adresse for familien Lang.
                </p>
                <p>
                  Da vi startede i 2005 var kloak og drænarbejde samt jord og beton og belægning, men det har ændret sig lidt da der er blevet meget gravearbejde for vandværker ved brud, skiftning af målerbrønde, afpropning af stik osv.
                </p>
                <p>
                  Udover kloak- og drænarbejde har vi også meget saltning og snerydning i vintermånederne ved parkeringsarealer for forretninger og virksomheder.
                </p>
              </div>
            </div>

            {/* Values / Core competencies */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center mb-3">
                  <i className="fa-solid fa-handshake text-base" aria-hidden="true" />
                </div>
                <h3 className="font-bold text-slate-900 text-base">Direkte mesterkontakt</h3>
                <p className="text-slate-600 text-xs sm:text-sm mt-1 leading-relaxed">
                  Hos os taler du direkte med den autoriserede kloakmester og entreprenør, der selv udfører arbejdet.
                </p>
              </div>

              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center mb-3">
                  <i className="fa-solid fa-award text-base" aria-hidden="true" />
                </div>
                <h3 className="font-bold text-slate-900 text-base">Garanti & sikkerhed</h3>
                <p className="text-slate-600 text-xs sm:text-sm mt-1 leading-relaxed">
                  Medlemskabet af Danske Kloakmestre sikrer kunden en tryg garantiordning på autoriseret kloakarbejde.
                </p>
              </div>
            </div>
          </div>

          {/* Right Visuals */}
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200 bg-white">
              <img
                src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/firma2.jpg', {
                  width: 800,
                  quality: 85,
                })}
                alt="Karlebyvej 32, 4894 Øster Ulslev"
                className="w-full h-72 object-cover"
              />
              <div className="p-5">
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
                  Hjemsted & værksted
                </span>
                <p className="text-slate-900 font-bold text-base mt-1">
                  Karlebyvej 32, 4894 Øster Ulslev
                </p>
                <p className="text-slate-600 text-xs sm:text-sm mt-1">
                  Herfra servicerer vi kunder på hele Lolland-Falster og tilstødende områder.
                </p>
              </div>
            </div>

            {/* Badges */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
              <h4 className="font-bold text-slate-900 text-base">Garanti & Certificering</h4>
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex flex-col items-center text-center">
                <img
                  src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/dme-kloakmestergaranti.webp', {
                    width: 440,
                    quality: 90,
                  })}
                  alt="DM&E Kloakmestergaranti"
                  className="h-24 w-auto object-contain mb-3"
                />
                <span className="text-xs font-semibold text-slate-700">DM&E Kloakmestergaranti</span>
              </div>
            </div>

            {/* Quick Contact Card */}
            <div className="bg-slate-900 text-white p-6 rounded-2xl space-y-3">
              <h4 className="font-bold text-base">Har du en opgave til os?</h4>
              <p className="text-slate-300 text-xs sm:text-sm">
                Ring direkte til Martin på 40 30 97 08 eller send en mail for en uforpligtende snak.
              </p>
              <div className="pt-2 flex flex-wrap gap-3">
                <a
                  href="tel:40309708"
                  className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone mr-1.5" aria-hidden="true" />
                  Ring op: 40 30 97 08
                </a>
                <Link
                  to="/kontakt-os"
                  className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs border border-slate-700"
                >
                  Send henvendelse
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Materials & Equipment Showcase */}
      <section className="py-16 bg-slate-100 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-2">
              <i className="fa-solid fa-truck-monster text-xs" aria-hidden="true" />
              Materiel & udstyr
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Eget udstyr til alle opgaver
            </h2>
            <p className="text-slate-600 text-sm sm:text-base mt-2">
              Vi råder over egne gravemaskiner, læssemaskiner og specialudstyr på værkstedet i Øster Ulslev.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm flex flex-col">
              <div className="h-60 overflow-hidden bg-slate-100">
                <img
                  src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/imgi-94-480335739-1127467075747587-2856452481133737084-n.jpg', {
                    width: 700,
                    height: 500,
                    fit: 'cover',
                  })}
                  alt="Kramer minilæsser med skovl på værkstedet"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-5 flex-1">
                <h3 className="font-bold text-slate-900 text-base">Eget værksted og maskinhal</h3>
                <p className="text-slate-600 text-xs sm:text-sm mt-1">
                  Kramer minilæsser og specialmateriel vedligeholdes løbende på basen i Øster Ulslev.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm flex flex-col">
              <div className="h-60 overflow-hidden bg-slate-100">
                <img
                  src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/imgi-94-481981910-1140782321082729-6555075428871264124-n.jpg', {
                    width: 700,
                    height: 500,
                    fit: 'cover',
                  })}
                  alt="Nyanlagt skovsti kantet med marksten og minigraver"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-5 flex-1">
                <h3 className="font-bold text-slate-900 text-base">Anlægs- og grusarbejde</h3>
                <p className="text-slate-600 text-xs sm:text-sm mt-1">
                  Naturstier og bundopbygning kantet med kampesten, udført med kompakt minigraver.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm flex flex-col">
              <div className="h-60 overflow-hidden bg-slate-100">
                <img
                  src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/imgi-94-613121285-1380287927132166-3828401678356812271-n.jpg', {
                    width: 700,
                    height: 500,
                    fit: 'cover',
                  })}
                  alt="Vintertjeneste og saltspreder ved kirke"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-5 flex-1">
                <h3 className="font-bold text-slate-900 text-base">Vinterdrift & saltning</h3>
                <p className="text-slate-600 text-xs sm:text-sm mt-1">
                  Udrustet med saltspreder og sneskovl til hurtig indsats på veje og pladser.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
